import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { PayApprovalHeaderStatus } from "./pay-approval-header-status";
import { PeriodSelector } from "./period-selector";
import { TechnicianStatementView } from "./technician-statement-view";
import type { PeriodOption } from "./types";

interface TechnicianWorkbookExperienceProps {
  businessId: string | null;
  runId: string;
  isLocked: boolean;
  isLoading: boolean;
  periodOptions: PeriodOption[];
  selectedPeriodStart?: string;
  onPeriodChange: (value: string) => void;
}

export const TechnicianWorkbookExperience = ({
  businessId,
  runId,
  isLocked,
  isLoading,
  periodOptions,
  selectedPeriodStart,
  onPeriodChange,
}: TechnicianWorkbookExperienceProps) => {
  if (isLoading) {
    return (
      <Stack gap={4} align="center" justify="center" className="h-page">
        <Spinner layout="centered" />
      </Stack>
    );
  }

  return (
    <Stack gap={0} align="stretch" className="h-page min-w-0 bg-surface-raised">
      <Stack align="start" gap={0} className="w-full shrink-0">
        {/* Rules on the column rather than the page: the pack is a centred
            column, and a full-bleed line reads as a stripe across empty space on
            anything wider than a phone. */}
        <Stack
          align="start"
          gap={0}
          className="mx-auto w-full max-w-2xl border-b border-line px-4 py-3"
        >
          <PeriodSelector
            options={periodOptions}
            value={selectedPeriodStart}
            onValueChange={onPeriodChange}
            /* Whether anything is being asked of them, in the strip that stays put
               while they read several screens of pay. On the label's row, so the
               period selector keeps the full width below it. */
            status={
              <PayApprovalHeaderStatus
                businessId={businessId}
                runId={runId}
                isLocked={isLocked}
              />
            }
          />
        </Stack>
      </Stack>

      {/* align="stretch" is load-bearing: `fullWidth` sizes this wrapper, but Stack
          still defaults to items-start, which left the pack itself content-sized —
          so its width tracked whatever the widest control in the footer happened to
          be, and a technician tapping "Approve my pay" watched their whole statement
          narrow by ~150px as the button was replaced by a line of text. */}
      <Stack gap={0} align="stretch" className="min-h-0 w-full flex-1">
        {/* Keyed on the run: a period change swaps `runId` in place, and without a
            remount an attached figure from the previous pack survived and could be
            posted against the new run — describing numbers it was never about. */}
        <TechnicianStatementView
          key={runId}
          businessId={businessId}
          runId={runId}
          isLocked={isLocked}
        />
      </Stack>
    </Stack>
  );
};
